import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AlertCircle, ArrowLeft, Loader2, RefreshCw } from "lucide-react";
import { type Product } from "@sakura-tech-lab/dashboard-api";
import useSWR from "swr";

import { ProductDescriptionCard } from "@/components/product-detail/ProductDescriptionCard";
import { ProductIngestionProgress } from "@/components/product-detail/ProductIngestionProgress";
import { ProductMediaGallery } from "@/components/product-detail/ProductMediaGallery";
import { ProductModerationCard } from "@/components/product-detail/ProductModerationCard";
import { ProductOffers } from "@/components/product-detail/ProductOffers";
import { ProductSourceSheet } from "@/components/product-detail/ProductSourceSheet";
import { ProductSpecificationsCard } from "@/components/product-detail/ProductSpecificationsCard";
import { ProductSummaryPanel } from "@/components/product-detail/ProductSummaryPanel";
import {
  availableLanguages,
  getProductTitle,
  getVariantImages,
  getVariantLabels,
} from "@/components/product-detail/product-detail-utils";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { isIngestionActive } from "@/lib/ingestion-progress";
import { useAuth } from "@/context/AuthContext";
import { CategoryService, type CategoryTreeNode } from "@/services/category.service";
import { getProduct } from "@/services/product.service";
import {
  CatalogueService,
  catalogueCacheKey,
  type Catalogue,
} from "@/services/catalogue.service";

async function fetchProduct([, catalogueId, productId]: readonly [
  "product",
  string,
  string,
]): Promise<Product> {
  return getProduct(catalogueId, productId);
}

async function fetchCategoryTree([, catalogueId]: readonly [
  "catalog-categories",
  string,
]): Promise<CategoryTreeNode[]> {
  return CategoryService.getTree(catalogueId);
}

interface VariantSelection {
  readonly productId: string;
  readonly variantId: string;
}

export default function ProductDetailPage() {
  const { catalogueId, productId } = useParams<{ catalogueId: string; productId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [variantSelection, setVariantSelection] = useState<VariantSelection | null>(null);
  const { data: catalogue, error: catalogueError, isLoading: isCatalogueLoading } = useSWR<Catalogue>(
    isAuthenticated && catalogueId ? catalogueCacheKey(catalogueId) : null,
    () => CatalogueService.getById(catalogueId!),
    {
      revalidateOnFocus: false,
      refreshInterval: (latest) => latest?.provisioningStatus === "provisioning" ? 5_000 : 0,
      refreshWhenHidden: false,
    },
  );
  const catalogueReady = catalogue?.provisioningStatus === "ready";
  const supportsProductRead = catalogueReady && (catalogue?.capabilities.includes("product.read") ?? false);
  const supportsCategories = catalogueReady && (catalogue?.capabilities.includes("categorization") ?? false);
  const supportsModeration = catalogueReady && (catalogue?.capabilities.includes("moderation") ?? false);

  const productKey =
    isAuthenticated && productId && catalogueId && supportsProductRead
      ? (["product", catalogueId, productId] as const)
      : null;

  const {
    data: product,
    error,
    isLoading,
    isValidating,
    mutate,
  } = useSWR<Product>(productKey, fetchProduct, {
    refreshInterval: (latestProduct) =>
      latestProduct && isIngestionActive(latestProduct) ? 5_000 : 0,
    refreshWhenHidden: false,
    revalidateOnFocus: false,
  });

  const categoryTreeKey =
    isAuthenticated && catalogueId && supportsCategories
      ? (["catalog-categories", catalogueId] as const)
      : null;
  const { data: categoryTree = [] } = useSWR<CategoryTreeNode[]>(
    categoryTreeKey,
    fetchCategoryTree,
    { revalidateOnFocus: false },
  );

  const categoryId = product?.categoryDetails?.[0]?.id;
  const { data: categoryAttributes } = useSWR(
    isAuthenticated && catalogueId && categoryId && supportsCategories
      ? ["category-attributes", catalogueId, categoryId]
      : null,
    () => CategoryService.getAttributes(catalogueId!, categoryId!),
    { revalidateOnFocus: false },
  );

  const variants = useMemo(() => product?.variants ?? [], [product]);
  const requestedVariantId =
    product && variantSelection && variantSelection.productId === product.id
      ? variantSelection.variantId
      : null;
  const selectedVariant =
    variants.find((variant) => variant.id === requestedVariantId) ?? variants[0] ?? null;
  const languages = useMemo(() => (product ? availableLanguages(product) : []), [product]);
  const images = useMemo(
    () => (product ? getVariantImages(product, selectedVariant) : []),
    [product, selectedVariant],
  );
  const variantLabels = useMemo(
    () => (product ? getVariantLabels(product, selectedLanguage) : new Map<string, string>()),
    [product, selectedLanguage],
  );
  const title = product
    ? getProductTitle(product, selectedVariant, selectedLanguage)
    : "Product detail";

  useEffect(() => {
    if (languages.length > 0 && !languages.includes(selectedLanguage)) {
      setSelectedLanguage(languages[0]);
    }
  }, [languages, selectedLanguage]);

  useEffect(() => {
    setSelectedImageIndex(0);
  }, [selectedVariant?.id]);

  useEffect(() => {
    if (selectedImageIndex >= images.length) setSelectedImageIndex(0);
  }, [images.length, selectedImageIndex]);

  const handleBack = () => {
    if (!catalogueId) {
      navigate("/catalog");
      return;
    }
    // Preserve the products table state (page/filters/sort/search) carried in
    // the query string so the list is exactly where the user left it.
    const query = searchParams.toString();
    navigate(`/catalog/${catalogueId}${query ? `?${query}` : ""}`);
  };

  if (isCatalogueLoading || isLoading) {
    return (
      <ProductDetailShell title="Loading product" onBack={handleBack}>
        <div className="flex h-72 items-center justify-center" role="status">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="sr-only">Loading product</span>
        </div>
      </ProductDetailShell>
    );
  }


  if (catalogueError && !catalogue) {
    return (
      <ProductDetailShell title="Product unavailable" onBack={handleBack}>
        <div className="mx-auto flex max-w-md flex-col items-center justify-center rounded-lg border bg-card p-8 text-center">
          <AlertCircle className="mb-4 h-10 w-10 text-muted-foreground" />
          <h1 className="text-xl font-semibold text-foreground">Catalogue could not be loaded</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Product access stays unavailable until the catalogue settings can be verified. Try again from the catalogue page.
          </p>
          <Button type="button" className="mt-6" onClick={handleBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to catalogue
          </Button>
        </div>
      </ProductDetailShell>
    );
  }

  if (catalogue && !catalogueReady) {
    return (
      <ProductDetailShell title="Product unavailable" onBack={handleBack}>
        <div className="mx-auto flex max-w-md flex-col items-center justify-center rounded-lg border bg-card p-8 text-center">
          <AlertCircle className="mb-4 h-10 w-10 text-muted-foreground" />
          <h1 className="text-xl font-semibold text-foreground">Product unavailable</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {catalogue.provisioningStatus === "provisioning"
              ? "This catalogue is still being prepared. Product access will be available when preparation finishes."
              : "Catalogue provisioning failed, so its products cannot be accessed."}
          </p>
          <Button type="button" className="mt-6" onClick={handleBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to catalog
          </Button>
        </div>
      </ProductDetailShell>
    );
  }
  if (!product) {
    return (
      <ProductDetailShell title="Product not found" onBack={handleBack}>
        <div className="mx-auto flex max-w-md flex-col items-center justify-center rounded-lg border bg-card p-8 text-center">
          <AlertCircle className="mb-4 h-10 w-10 text-muted-foreground" />
          <h1 className="text-xl font-semibold text-foreground">Product not found</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {error
              ? "The product could not be loaded. Check your connection and try again."
              : "The product does not exist or is unavailable."}
          </p>
          <Button type="button" className="mt-6" onClick={handleBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to catalog
          </Button>
        </div>
      </ProductDetailShell>
    );
  }

  return (
    <ProductDetailShell
      title={title}
      onBack={handleBack}
      onRefresh={() => void mutate()}
      isRefreshing={isValidating}
      isAutoRefreshing={isIngestionActive(product)}
      actions={
        <ProductSourceSheet
          product={product}
          selectedVariant={selectedVariant}
          language={selectedLanguage}
        />
      }
    >
      {error ? (
        <div
          role="alert"
          className="mb-6 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
        >
          <AlertCircle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
          The latest refresh failed. Showing the most recently loaded product data.
        </div>
      ) : null}

      <div className="mx-auto grid w-full max-w-screen-2xl min-w-0 gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:items-start">
        <div className="min-w-0 self-start lg:sticky lg:top-6">
          <ProductMediaGallery
            images={images}
            productName={title}
            selectedImageIndex={selectedImageIndex}
            onSelectImage={setSelectedImageIndex}
          />
        </div>

        <div className="min-w-0 space-y-6">
          <ProductIngestionProgress product={product} />
          <ProductSummaryPanel
            product={product}
            selectedVariant={selectedVariant}
            categoryTree={categoryTree}
            variantLabels={variantLabels}
            language={selectedLanguage}
            languages={languages}
            onLanguageChange={setSelectedLanguage}
            onVariantChange={(variantId) =>
              setVariantSelection({ productId: product.id, variantId })
            }
          />
        </div>

        <div className="min-w-0 space-y-6 lg:col-span-2">
          <ProductDescriptionCard
            product={product}
            selectedVariant={selectedVariant}
            language={selectedLanguage}
          />
          <ProductSpecificationsCard
            product={product}
            selectedVariant={selectedVariant}
            language={selectedLanguage}
            attributeSchema={categoryAttributes?.resolved?.attributes}
          />
          <ProductOffers
            product={product}
            language={selectedLanguage}
            variantLabels={variantLabels}
          />
          {supportsModeration ? (
            <ProductModerationCard product={product} />
          ) : null}
        </div>
      </div>
    </ProductDetailShell>
  );
}

function ProductDetailShell({
  title,
  onBack,
  onRefresh,
  isRefreshing = false,
  isAutoRefreshing = false,
  actions,
  children,
}: {
  readonly title: string;
  readonly onBack: () => void;
  readonly onRefresh?: () => void;
  readonly isRefreshing?: boolean;
  readonly isAutoRefreshing?: boolean;
  readonly actions?: ReactNode;
  readonly children: ReactNode;
}) {
  return (
    <div className="min-w-0">
      <div className="mb-6 flex min-w-0 flex-wrap items-center gap-3">
        <Button type="button" variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to catalog
        </Button>
        <p className="min-w-0 flex-1 truncate text-sm text-muted-foreground">{title}</p>
        {isAutoRefreshing ? (
          <span className="text-xs text-muted-foreground" aria-live="polite">
            Updates automatically while processing
          </span>
        ) : null}
        {actions}
        {onRefresh ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isRefreshing}
            onClick={onRefresh}
          >
            <RefreshCw
              aria-hidden="true"
              className={cn("h-4 w-4", isRefreshing && "animate-spin")}
            />
            {isRefreshing ? "Refreshing" : "Refresh"}
          </Button>
        ) : null}
      </div>
      {children}
    </div>
  );
}
